import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { isAdminSessionExpiredError } from "../../lib/adminAuth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Plus, Trash2, Edit3, Upload, Volume2, X, Play, Square } from "lucide-react";

const EMPTY_QUESTION = {
  id: null,
  text: "",
  expected_answer: "",
  is_active: true,
  think_time_seconds: 5,
  audio_path: null,
};

function shortFileName(name, max = 42) {
  const s = String(name || "");
  if (s.length <= max) return s;
  const ext = s.includes(".") ? s.slice(s.lastIndexOf(".")) : "";
  const keep = Math.max(8, max - ext.length - 1);
  return `${s.slice(0, keep)}…${ext}`;
}

const QuestionsTab = () => {
  const [questions, setQuestions] = useState([]);
  const [editingQ, setEditingQ] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const fileInputRef = useRef(null);
  const previewAudioRef = useRef(null);
  const previewUrlRef = useRef(null);

  const activeCount = questions.filter((q) => q.is_active).length;

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/questions");
      setQuestions(data.questions);
    } catch (err) {
      if (isAdminSessionExpiredError(err)) return;
      toast.error("Erreur de chargement des questions");
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const stopPreview = useCallback(() => {
    const el = previewAudioRef.current;
    if (el) {
      el.pause();
      el.removeAttribute("src");
      el.load();
      previewAudioRef.current = null;
    }
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewPlaying(false);
  }, []);

  useEffect(() => {
    if (!dialogOpen) stopPreview();
  }, [dialogOpen, stopPreview]);

  useEffect(() => () => stopPreview(), [stopPreview]);

  const openNew = () => {
    stopPreview();
    setEditingQ({ ...EMPTY_QUESTION });
    setAudioFile(null);
    setDialogOpen(true);
  };

  const openEdit = (q) => {
    stopPreview();
    setEditingQ({
      ...q,
      expected_answer: q.expected_answer || "",
      think_time_seconds: q.think_time_seconds ?? 5,
    });
    setAudioFile(null);
    setDialogOpen(true);
  };

  const previewAudio = async () => {
    if (previewPlaying) {
      stopPreview();
      return;
    }

    stopPreview();
    try {
      let objectUrl = null;
      if (audioFile) {
        objectUrl = URL.createObjectURL(audioFile);
      } else if (editingQ?.id && editingQ.audio_path) {
        const { data } = await api.get(`/admin/questions/${editingQ.id}/audio`, {
          responseType: "blob",
        });
        objectUrl = URL.createObjectURL(data);
      } else {
        toast.error("Aucun fichier audio à écouter");
        return;
      }

      previewUrlRef.current = objectUrl;
      const el = new Audio(objectUrl);
      previewAudioRef.current = el;
      el.onended = () => {
        stopPreview();
      };
      el.onerror = () => {
        stopPreview();
        toast.error("Impossible de lire l'audio");
      };
      setPreviewPlaying(true);
      await el.play();
    } catch (err) {
      stopPreview();
      if (isAdminSessionExpiredError(err)) return;
      toast.error("Lecture audio impossible");
    }
  };

  const save = async () => {
    if (!editingQ.text.trim()) return toast.error("Texte requis");
    const thinkTime = Number(editingQ.think_time_seconds) || 5;
    if (thinkTime < 1 || thinkTime > 30) return toast.error("Temps de réponse entre 1 et 30 secondes");

    try {
      const body = {
        text: editingQ.text,
        expected_answer: editingQ.expected_answer?.trim() || null,
        is_active: editingQ.is_active,
        think_time_seconds: thinkTime,
      };
      let savedId = editingQ.id;
      if (editingQ.id) {
        await api.put(`/admin/questions/${editingQ.id}`, body);
        toast.success("Question mise à jour");
      } else {
        const { data } = await api.post("/admin/questions", body);
        savedId = data.id;
        toast.success("Question créée");
      }

      if (audioFile && savedId) {
        const form = new FormData();
        form.append("file", audioFile);
        await api.post(`/admin/questions/${savedId}/audio`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Audio enregistré");
      }

      setDialogOpen(false);
      refresh();
    } catch (err) {
      if (isAdminSessionExpiredError(err)) return;
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette question ?")) return;
    try {
      await api.delete(`/admin/questions/${id}`);
      toast.success("Question supprimée");
      refresh();
    } catch (err) {
      if (isAdminSessionExpiredError(err)) return;
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const toggleActive = async (q, nextActive) => {
    setTogglingId(q.id);
    setQuestions((prev) =>
      prev.map((item) => (item.id === q.id ? { ...item, is_active: nextActive } : item)),
    );
    try {
      await api.put(`/admin/questions/${q.id}`, { is_active: nextActive });
      toast.success(nextActive ? "Question activée" : "Question désactivée");
    } catch (err) {
      setQuestions((prev) =>
        prev.map((item) => (item.id === q.id ? { ...item, is_active: q.is_active } : item)),
      );
      if (isAdminSessionExpiredError(err)) return;
      toast.error(err?.response?.data?.detail || "Impossible de modifier le statut");
    } finally {
      setTogglingId(null);
    }
  };

  const removeAudio = async (qid) => {
    try {
      await api.delete(`/admin/questions/${qid}/audio`);
      toast.success("Audio supprimé");
      if (editingQ?.id === qid) {
        setEditingQ((prev) => (prev ? { ...prev, audio_path: null } : prev));
      }
      refresh();
    } catch (err) {
      if (isAdminSessionExpiredError(err)) return;
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const uploadAudioInline = async (qid, file) => {
    if (!file) return;
    setUploadingId(qid);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/admin/questions/${qid}/audio`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Audio mis à jour");
      refresh();
    } catch (err) {
      if (isAdminSessionExpiredError(err)) return;
      toast.error(err?.response?.data?.detail || "Erreur upload audio");
    } finally {
      setUploadingId(null);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-white font-semibold">Questions ouvertes</h3>
          <p className="text-white/45 text-sm">
            {activeCount} active{activeCount !== 1 ? "s" : ""} · réponse orale · chrono par défaut 5 s
          </p>
        </div>
        <Button onClick={openNew} className="bg-md-cyan text-md-navy hover:bg-md-cyan/90" data-testid="add-question">
          <Plus size={16} className="mr-1" /> Nouvelle
        </Button>
      </div>

      <div className="space-y-2">
        {questions.map((q) => (
          <div
            key={q.id}
            className="rounded-xl border border-white/10 bg-white/[0.03] p-3 flex items-start gap-3"
            data-testid={`question-row-${q.id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="text-white text-sm font-medium truncate">{q.text}</div>
              <div className="text-white/40 text-xs mt-1 flex flex-wrap gap-x-3 gap-y-1">
                <span>Réponse attendue : {q.expected_answer || "—"}</span>
                <span>{q.think_time_seconds ?? 5}s</span>
                <span className={q.audio_path ? "text-emerald-400" : "text-amber-400/80"}>
                  {q.audio_path ? "Audio OK" : "Sans audio"}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Switch
                checked={Boolean(q.is_active)}
                disabled={togglingId === q.id}
                onCheckedChange={(v) => toggleActive(q, v)}
              />
              <label className="cursor-pointer p-1.5 rounded-lg hover:bg-white/10 text-white/60" title="Upload audio">
                <Upload size={15} className={uploadingId === q.id ? "animate-pulse text-md-cyan" : ""} />
                <input
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadAudioInline(q.id, f);
                    e.target.value = "";
                  }}
                />
              </label>
              <button
                type="button"
                className="p-1.5 rounded-lg hover:bg-white/10 text-white/60"
                onClick={() => openEdit(q)}
              >
                <Edit3 size={15} />
              </button>
              <button
                type="button"
                className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-400/80"
                onClick={() => remove(q.id)}
              >
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
        {!questions.length && (
          <p className="text-white/40 text-sm text-center py-8">Aucune question pour le moment.</p>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0d1520] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingQ?.id ? "Modifier la question" : "Nouvelle question"}</DialogTitle>
          </DialogHeader>
          {editingQ && (
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-white/70">Question</Label>
                <Textarea
                  value={editingQ.text}
                  onChange={(e) => setEditingQ({ ...editingQ, text: e.target.value })}
                  className="mt-1 bg-white/5 border-white/10 min-h-[80px]"
                  placeholder="Texte lu / affiché…"
                />
              </div>
              <div>
                <Label className="text-white/70">Réponse attendue (admin)</Label>
                <Input
                  value={editingQ.expected_answer}
                  onChange={(e) => setEditingQ({ ...editingQ, expected_answer: e.target.value })}
                  className="mt-1 bg-white/5 border-white/10"
                  placeholder="Pour le débrief casting"
                />
              </div>
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <Label className="text-white/70">Temps sans réponse (s)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={30}
                    value={editingQ.think_time_seconds}
                    onChange={(e) =>
                      setEditingQ({ ...editingQ, think_time_seconds: Number(e.target.value) })
                    }
                    className="mt-1 bg-white/5 border-white/10"
                  />
                </div>
                <div className="flex items-center gap-2 pb-2">
                  <Switch
                    checked={Boolean(editingQ.is_active)}
                    onCheckedChange={(v) => setEditingQ({ ...editingQ, is_active: v })}
                  />
                  <span className="text-sm text-white/60">Active</span>
                </div>
              </div>
              <div>
                <Label className="text-white/70 flex items-center gap-2">
                  <Volume2 size={14} /> Audio animateur (question lue)
                </Label>
                <div className="mt-2 rounded-xl border border-white/10 bg-white/[0.04] p-3 space-y-2.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="border-white/15 shrink-0"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload size={14} className="mr-1.5" />
                      {audioFile || editingQ.audio_path ? "Remplacer" : "Choisir un fichier"}
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      onChange={(e) => {
                        stopPreview();
                        setAudioFile(e.target.files?.[0] || null);
                        e.target.value = "";
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="border-white/15 shrink-0"
                      disabled={!audioFile && !editingQ.audio_path}
                      onClick={() => void previewAudio()}
                      title={previewPlaying ? "Arrêter" : "Écouter"}
                    >
                      {previewPlaying ? (
                        <>
                          <Square size={14} className="mr-1.5" /> Arrêter
                        </>
                      ) : (
                        <>
                          <Play size={14} className="mr-1.5" /> Écouter
                        </>
                      )}
                    </Button>
                    {editingQ.audio_path && !audioFile && (
                      <Button
                        type="button"
                        variant="ghost"
                        className="text-red-300 hover:text-red-200 shrink-0"
                        onClick={() => {
                          stopPreview();
                          removeAudio(editingQ.id);
                        }}
                      >
                        <X size={14} className="mr-1" /> Supprimer
                      </Button>
                    )}
                  </div>
                  <p
                    className="text-xs text-white/55 truncate min-w-0"
                    title={
                      audioFile?.name
                        || (editingQ.audio_path ? "Audio déjà stocké sur le serveur" : "Aucun fichier")
                    }
                  >
                    {audioFile
                      ? shortFileName(audioFile.name)
                      : editingQ.audio_path
                        ? "Audio déjà stocké — utilisez « Écouter » pour vérifier"
                        : "Aucun fichier sélectionné (mp3, wav, m4a…)"}
                  </p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={save} className="bg-md-cyan text-md-navy hover:bg-md-cyan/90">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QuestionsTab;
